import React, { useId, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiExternalLink } from 'react-icons/fi';
import { useI18n } from '../../../i18n';
import type { ComplianceIssue, FixOption } from '../../../services/rosterCompliance';
import {
  candidateNotes,
  fixSummary,
  fixTitle,
  guardLabel,
  regulationUrl,
  ruleTitle,
  unsupportedText,
  verificationBadge,
} from './fixPresentation';

interface IssueFixCardProps {
  issue: ComplianceIssue;
  selectedOptionId: string | null;
  /** Label of the breach whose chosen fix already clears this one, if any. */
  coveredByLabel: string | null;
  onChoose: (optionId: string | null) => void;
}

/** Enough to compare without scrolling; the rest sit behind "more options". */
const VISIBLE_OPTIONS = 3;

function FixOptionChoice({
  issue,
  option,
  groupName,
  checked,
  recommended,
  onChoose,
}: {
  issue: ComplianceIssue;
  option: FixOption;
  groupName: string;
  checked: boolean;
  recommended: boolean;
  onChoose: (optionId: string) => void;
}) {
  const { t, tn } = useI18n();
  const tr = { t, tn };
  const badge = verificationBadge(option.verification, issue.violation, tr);
  const notes = option.candidate ? candidateNotes(option.candidate, tr) : [];
  const leavesGap = option.uncoveredMinutes > 0;

  return (
    <label className={`cfx-option${checked ? ' is-selected' : ''}`}>
      <input
        type="radio"
        className="cfx-option__input"
        name={groupName}
        checked={checked}
        onChange={() => onChoose(option.id)}
      />
      <span className="cfx-option__body">
        <span className="cfx-option__title">
          {fixTitle(option, tr)}
          {recommended && <span className="cfx-pill">{t('Recommended')}</span>}
        </span>
        <span className={`cfx-option__summary${leavesGap ? ' cfx-option__summary--warning' : ''}`}>
          {fixSummary(option, tr)}
        </span>
        <span className="cfx-option__meta">
          <span className={`cfx-badge cfx-badge--${badge.tone}`}>
            {badge.tone === 'success'
              ? <FiCheckCircle aria-hidden="true" />
              : <FiAlertTriangle aria-hidden="true" />}
            {badge.text}
          </span>
          {notes.map((note) => <span key={note} className="cfx-option__note">{note}</span>)}
        </span>
        {option.verification.introduced.length > 0 && (
          <span className="cfx-option__introduced">
            {option.verification.introduced.map((violation) => violation.message).join(' ')}
          </span>
        )}
      </span>
    </label>
  );
}

/** One breach and the ways to fix it. Options are a native radio group, so arrow keys work. */
export default function IssueFixCard({ issue, selectedOptionId, coveredByLabel, onChoose }: IssueFixCardProps) {
  const { t, tn } = useI18n();
  const tr = { t, tn };
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const [showAll, setShowAll] = useState(false);

  const law = regulationUrl(issue.violation);
  const selectedIndex = issue.options.findIndex((option) => option.id === selectedOptionId);
  // Never hide the option that is currently chosen.
  const visibleCount = showAll ? issue.options.length : Math.max(VISIBLE_OPTIONS, selectedIndex + 1);
  const visibleOptions = issue.options.slice(0, visibleCount);
  const hiddenCount = issue.options.length - visibleOptions.length;

  return (
    <article className="cfx-issue" aria-labelledby={titleId}>
      <header className="cfx-issue__head">
        <span className="cfx-issue__icon" aria-hidden="true"><FiAlertTriangle /></span>
        <div className="cfx-issue__heading">
          <h3 id={titleId} className="cfx-issue__title">{ruleTitle(issue.violation, tr)}</h3>
          <p className="cfx-issue__guard">{guardLabel(issue.violation, tr)}</p>
        </div>
        {law && (
          <a className="cfx-issue__law" href={law} target="_blank" rel="noopener noreferrer">
            {t('Regulation')}
            <FiExternalLink aria-hidden="true" />
            <span className="cfx-sr-only">{t('(opens in a new tab)')}</span>
          </a>
        )}
      </header>

      <p className="cfx-issue__message">{issue.violation.message}</p>

      {coveredByLabel && (
        <p className="cfx-issue__covered">
          <FiCheckCircle aria-hidden="true" />
          {t('Already cleared by your fix for {issue}.', { issue: coveredByLabel })}
        </p>
      )}

      {issue.options.length > 0 ? (
        <fieldset className="cfx-options">
          <legend className="cfx-sr-only">
            {t('Fix for {rule}, {guard}', { rule: ruleTitle(issue.violation, tr), guard: guardLabel(issue.violation, tr) })}
          </legend>

          {visibleOptions.map((option) => (
            <FixOptionChoice
              key={option.id}
              issue={issue}
              option={option}
              groupName={baseId}
              checked={option.id === selectedOptionId}
              // Once another fix clears this breach, leaving it alone is the recommendation.
              recommended={!coveredByLabel && issue.recommendedOptionId === option.id}
              onChoose={onChoose}
            />
          ))}

          {hiddenCount > 0 && (
            <button type="button" className="cfx-link" onClick={() => setShowAll(true)}>
              {tn('Show {count} more option', 'Show {count} more options', hiddenCount)}
            </button>
          )}

          <label className={`cfx-option cfx-option--skip${selectedOptionId === null ? ' is-selected' : ''}`}>
            <input
              type="radio"
              className="cfx-option__input"
              name={baseId}
              checked={selectedOptionId === null}
              onChange={() => onChoose(null)}
            />
            <span className="cfx-option__body">
              <span className="cfx-option__title">{t('Leave as is')}</span>
            </span>
          </label>
        </fieldset>
      ) : (
        <p className="cfx-issue__unsupported">
          {unsupportedText(issue.unsupportedReason ?? 'no_verified_fix', tr)}
        </p>
      )}
    </article>
  );
}
